import {ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit} from '@angular/core';
import {Location as BrowserLocation} from '@angular/common';
import {FormBuilder, FormGroup, Validators,} from '@angular/forms';
import {PATRON_NOTIFICATION_PREFERENCE_TYPE, PatronNotificationPreference, RoutesService} from '@raven';
import {CommunicationPreferences} from '../../../model/patron/communication-preferences';
import {PatronSelectors} from '@store/store.selectors';
import {snapshot} from '@store/store.helpers';
import {Patron} from '@store/patron/patron.types';
import {PatronService} from '../../../services/patron.service';
import {SelectOptions, STATE_SELECT} from '@store/common/common.types';
import {isPopulatedString} from '@store/common/typing.helpers';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {PhonePipe} from '../../shared/pipes/phone.pipe';

@Component({
  selector: 'rn-notification-preferences',
  templateUrl: './notification-preferences.component.html',
  styleUrl: './notification-preferences.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class NotificationPreferencesComponent implements OnInit {
  protected readonly STATE_SELECT = STATE_SELECT;
  private readonly destroyRef = inject(DestroyRef);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  patron: Patron = snapshot(PatronSelectors.Auth.patron);
  notificationPreferencesForm: FormGroup;
  preferences: CommunicationPreferences | null = null;
  public readonly NOTIFICATION_PREFERENCE_SELECTION: SelectOptions<string> = [
    {displayName: 'Email', value: 'email'},
    {displayName: 'SMS Text', value: 'sms'},
    {displayName: 'In-App Only', value: 'in-app'}
  ];
  public disabledPreferences: string[] = [];
  public notificationPhoneOptions: SelectOptions<string> = [];
  private readonly notificationPreferenceFieldNames = [
    'accountNotifications',
    'expirationNotifications',
    'renewNotifications',
    'holdNotifications',
    'receiptNotifications',
    'noticeNotifications',
    'itemRequestNotifications'
  ];

  constructor(
    public routingService: RoutesService,
    private browserLocation: BrowserLocation,
    private fb: FormBuilder,
    private patronService: PatronService,
    public phonePipe: PhonePipe
  ) {
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  initializeForm(): void {
    this.buildNotificationPhoneOptions();
    this.createForm();
    this.reloadCommunicationPreferences();
  }

  private reloadCommunicationPreferences(): void {
    this.patronService.getCommunicationPreferences()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((preferences) => this.applyCommunicationPreferences(preferences));
  }

  private applyCommunicationPreferences(preferences: CommunicationPreferences): void {
    this.preferences = preferences;
    this.notificationPreferencesForm.reset({
      notificationEmail: preferences.notificationEmail,
      phoneSms: preferences.phoneSms,
      smsTermsAccepted: preferences.smsTermsAccepted,
      accountNotifications: this.getSelectOption(this.findNotification(preferences.notifications, PATRON_NOTIFICATION_PREFERENCE_TYPE.ACCOUNT)),
      expirationNotifications: this.getSelectOption(this.findNotification(preferences.notifications, PATRON_NOTIFICATION_PREFERENCE_TYPE.CARD_EXPIRE)),
      renewNotifications: this.getSelectOption(this.findNotification(preferences.notifications, PATRON_NOTIFICATION_PREFERENCE_TYPE.AUTO_RENEW)),
      holdNotifications: this.getSelectOption(this.findNotification(preferences.notifications, PATRON_NOTIFICATION_PREFERENCE_TYPE.HOLD)),
      receiptNotifications: this.getSelectOption(this.findNotification(preferences.notifications, PATRON_NOTIFICATION_PREFERENCE_TYPE.RECEIPT)),
      noticeNotifications: this.getSelectOption(this.findNotification(preferences.notifications, PATRON_NOTIFICATION_PREFERENCE_TYPE.NOTICE)),
      itemRequestNotifications: this.getSelectOption(this.findNotification(preferences.notifications, PATRON_NOTIFICATION_PREFERENCE_TYPE.ITEM_REQUEST)),
    });
    this.updateDisabledSelection();
    this.updateSmsTermsAcceptedControl();
    this.notificationPreferencesForm.markAsPristine();
    this.notificationPreferencesForm.markAsUntouched();
    this.changeDetectorRef.markForCheck();
  }

  findNotification(notifications: PatronNotificationPreference[], type: PATRON_NOTIFICATION_PREFERENCE_TYPE): PatronNotificationPreference {
    if (notifications) {
      for (const notification of notifications) {
        if (notification.notificationType === type) {
          return notification;
        }
      }
    }
    // default value if no preferences exist
    return new PatronNotificationPreference();
  }

  getSelectOption(notification: PatronNotificationPreference) {
    if (notification.notifyText) return 'sms';
    if (notification.notifyEmail) return 'email';
    return 'in-app';
  }

  formatPhoneNumber(phone: string): string {
    const phoneSms = phone?.replace(/\D/g, '').slice(-10);
    return this.phonePipe.transform(phoneSms)
  }

  buildNotificationPhoneOptions(): void {
    this.notificationPhoneOptions = [];
    const phoneSms = this.patron.phoneSms;
    const formattedPhoneSms = this.formatPhoneNumber(phoneSms);
    if (isPopulatedString(phoneSms)) {
      this.notificationPhoneOptions.push({displayName: this.formatPhoneNumber(phoneSms), value: phoneSms});
    }
    const phone1 = this.patron.phone1;
    const formattedPhone1 = this.formatPhoneNumber(phone1);
    if (this.patron.phone1Mobile && isPopulatedString(phone1) && formattedPhoneSms !== formattedPhone1) {
      this.notificationPhoneOptions.push({displayName: formattedPhone1, value: phone1});
    }
    const phone2 = this.patron.phone2;
    const formattedPhone2 = this.formatPhoneNumber(phone2);
    if (this.patron.phone2Mobile && isPopulatedString(phone2) && formattedPhoneSms !== formattedPhone2) {
      this.notificationPhoneOptions.push({displayName: formattedPhone2, value: phone2});
    }
  }

  createForm(): void {
    this.notificationPreferencesForm = this.fb.group({
      notificationEmail: [this.patron.notificationEmail || this.patron.email, [Validators.email, Validators.maxLength(256)]],
      phoneSms: [this.patron.phoneSms, []],
      smsTermsAccepted: [this.patron.smsTermsAccepted, []],
      accountNotifications: ['email', []],
      expirationNotifications: ['email', []],
      renewNotifications: ['email', []],
      holdNotifications: ['email', []],
      receiptNotifications: ['email', []],
      noticeNotifications: ['email', []],
      itemRequestNotifications: ['email', []],
      // localEvents: ['email', []],
      // localEventsToggle: [true, []],
      // monthlyNewsletter: ['email', []],
      // monthlyNewsletterToggle: [true, []],
    });
    this.updateDisabledSelection();
    this.notificationPreferencesForm.get('phoneSms').valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateSmsTermsAcceptedControl();
        this.updateDisabledSelection();
      });
    this.notificationPreferencesForm.get('smsTermsAccepted').valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateDisabledSelection());
  }

  updateDisabledSelection() {
    const formValues = this.notificationPreferencesForm.getRawValue();
    this.disabledPreferences = isPopulatedString(formValues.phoneSms) && formValues.smsTermsAccepted ? [] : ['SMS Text'];
  }

  private updateSmsTermsAcceptedControl(): void {
    const smsTermsAcceptedControl = this.notificationPreferencesForm.get('smsTermsAccepted');
    if (this.hasSmsNumberSelected()) {
      smsTermsAcceptedControl.enable({emitEvent: false});
    } else {
      smsTermsAcceptedControl.setValue(false, {emitEvent: false});
      smsTermsAcceptedControl.disable({emitEvent: false});
    }
  }

  cancel(): void {
    // head back to whence we came
    this.browserLocation.back();
  }

  selectionToNotificationPreference(type: PATRON_NOTIFICATION_PREFERENCE_TYPE, selectValue: string, toggle = true): PatronNotificationPreference {
    return {
      patronId: this.patron.id,
      notificationType: type,
      sendNotification: toggle,
      notifyEmail: selectValue === 'email',
      notifyText: selectValue === 'sms',
      notifyPhone: false,
    }
  }

  submit(): void {
    if (!this.canSave()) return;

    const formValues = this.notificationPreferencesForm.getRawValue();
    const notifications = [
      this.selectionToNotificationPreference(PATRON_NOTIFICATION_PREFERENCE_TYPE.ACCOUNT, formValues.accountNotifications),
      this.selectionToNotificationPreference(PATRON_NOTIFICATION_PREFERENCE_TYPE.CARD_EXPIRE, formValues.expirationNotifications),
      this.selectionToNotificationPreference(PATRON_NOTIFICATION_PREFERENCE_TYPE.AUTO_RENEW, formValues.renewNotifications),
      this.selectionToNotificationPreference(PATRON_NOTIFICATION_PREFERENCE_TYPE.HOLD, formValues.holdNotifications),
      this.selectionToNotificationPreference(PATRON_NOTIFICATION_PREFERENCE_TYPE.RECEIPT, formValues.receiptNotifications),
      this.selectionToNotificationPreference(PATRON_NOTIFICATION_PREFERENCE_TYPE.NOTICE, formValues.noticeNotifications),
      this.selectionToNotificationPreference(PATRON_NOTIFICATION_PREFERENCE_TYPE.ITEM_REQUEST, formValues.itemRequestNotifications),
    ];
    const preferences: CommunicationPreferences = {
      notifications,
      notificationEmail: formValues.notificationEmail,
      phoneSms: formValues.phoneSms,
      smsTermsAccepted: formValues.smsTermsAccepted,
      unsubscribeAll: false
    };
    this.patronService
      .updateCommunicationPreferences(preferences, {
        successMessage: 'Preferences updated Successfully.',
        errorMessage: 'Error updating your communication preferences. Please contact customer support.',
        onSuccess: () => {
          this.reloadCommunicationPreferences();
        }
      });
  }

  hasSmsNumberSelected(): boolean {
    return isPopulatedString(this.notificationPreferencesForm.getRawValue().phoneSms);
  }

  hasSmsOption(): boolean {
    return this.notificationPreferenceFieldNames.some((field) => this.notificationPreferencesForm.getRawValue()[field] === 'sms');
  }

  canSave(): boolean {
    const formValues = this.notificationPreferencesForm.getRawValue();
    const hasPhoneSms = isPopulatedString(formValues.phoneSms);
    const hasAcceptedSmsTerms = formValues.smsTermsAccepted === true;
    const hasSmsPreference = this.hasSmsOption();

    if (this.notificationPreferencesForm.invalid) return false;
    if (hasPhoneSms && !hasAcceptedSmsTerms) return false;
    return !(hasSmsPreference && (!hasPhoneSms || !hasAcceptedSmsTerms));

  }
}
